import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import sortByLodash from 'lodash/sortBy';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import {window} from 'ssr-window';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import AlertModal from 'common/alert/AlertModal';
import SHLoader from 'common/loader/SHLoader';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import QuickFilters from 'filters/QuickFilters';
import {setNoSpotsFound, toggleMobileListView} from 'search/search-actions';
import {setLocation} from 'search-request/search-request-actions';
import {getSpotsMobile} from 'spots/spots-actions';
import {SEARCH_TYPE} from './search-prop-types';
import WithSearch from './WithSearch';
import SpotListContainer from './spot-list/SpotListContainer';
import SpotListSearchControlsCondensed from './spot-list/SpotListSearchControlsCondensed';

const SpotDetails = loadable(() => import('spot-details/SpotDetails'));
const SearchMap = loadable(() => import('./map/SearchMap'));
const EventList = loadable(() => import('event/EventList'));
const DestinationInfoModalMobile = loadable(() =>
    import('destination/DestinationInfoModalMobile')
);

export class SearchMobile extends Component {
    static propTypes = {
        searchRequestId: PropTypes.string,
        searchUrl: PropTypes.string,
        searchType: SEARCH_TYPE.isRequired,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        spots: PropTypes.arrayOf(PropTypes.object),
        findParkingNearMe: PropTypes.func,
        selectedSpot: PropTypes.object,
        loadMobileSpots: PropTypes.func.isRequired,
        isFetchComplete: PropTypes.bool.isRequired,
        numActiveFilters: PropTypes.number,
        idealDistance: PropTypes.number,
        mobilePaginationLimit: PropTypes.number,
        isMobileListViewActive: PropTypes.bool.isRequired,
        sortBy: PropTypes.string.isRequired,
        geolocated: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        onToggleEventList: PropTypes.func,
        setNewSearchLocation: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        showMobileListView: PropTypes.func.isRequired,
        onShowDetailsClick: PropTypes.func.isRequired,
        onNoSpotsFound: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        const {geolocated, searchUrl} = props;

        this._minDistance = 0;
        this._pageCount = 1;
        this._spotLoaderEnabled = true;

        // TODO: update for new URL structure?
        // In order to test effect of content on SEO and conversion, only show on these pages
        this._hasSeoContent = [
            '/chicago-parking',
            '/nyc-parking',
            '/philadelphia-parking',
        ].includes(searchUrl);

        this.state = {
            showListLoader: false,
            showLoadingComplete: false,
            showGeolocateModal: EnvironmentUtils.isBrowser()
                ? Boolean(
                      !geolocated &&
                          window.location.pathname.match(/parking-near-me/)
                  )
                : false,
            showInfoModal: false,
        };
    }

    componentDidMount() {
        if (
            !this.state.showGeolocateModal &&
            this.props.isMobileListViewActive
        ) {
            this._newSearch({
                initial: true,
            });
        }

        this._cacheSelectors();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isFetchComplete,
            isMonthly,
            searchRequestId,
            starts,
            ends,
            isMobileListViewActive,
            geolocated,
        } = this.props;
        const {
            isFetchComplete: prevIsFetchComplete,
            isMonthly: prevIsMonthly,
            searchRequestId: prevSearchRequestId,
            starts: prevStarts,
            ends: prevEnds,
            isMobileListViewActive: prevIsMobileListViewActive,
            geolocated: prevGeolocated,
        } = prevProps;

        if (!prevIsFetchComplete && isFetchComplete) {
            this._fetchCompleted();
        }

        if (prevSearchRequestId !== searchRequestId) {
            this._newSearch({
                initial: false,
            });
        } else if (prevIsMonthly !== isMonthly) {
            this._minDistance = 0;
            this._pageCount = 1;
            this._newSearch({
                initial: false,
            });
        } else {
            if (
                (isMonthly && prevStarts !== starts) ||
                (!isMonthly && (prevStarts !== starts || prevEnds !== ends))
            ) {
                this._minDistance = 0;
                this._pageCount = 1;

                this._newSearch({
                    dateTimeChanged: true,
                });
            }
        }

        if (!prevIsMobileListViewActive && isMobileListViewActive) {
            this._cacheSelectors();
        }

        if (!prevGeolocated && geolocated) {
            this._newSearch({
                initial: true,
            });
        }
    }

    componentWillUnmount() {
        if (this._spotsLoader) {
            this._spotsLoader.cancel();
        }

        this._spotLoaderEnabled = false;
    }

    _onConfirmGeolocate = () => {
        this.setState({
            showListLoader: true,
        });

        if (!isUndefined(window) && window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(
                this._userPositionReturned,
                () => {
                    // eslint-disable-next-line no-alert
                    alert(
                        'Sorry, your browser does not allow geolocation, please search an address.'
                    );

                    this._onRejectGeolocate();
                }
            );
        } else {
            // eslint-disable-next-line no-alert
            alert(
                'Sorry, your browser does not support geolocation, please search an address.'
            );

            this._onRejectGeolocate();
        }
    };

    _onRejectGeolocate = () => {
        this._onLoadingComplete();
    };

    _onLoadingComplete = () => {
        this.setState({
            showListLoader: false,
            showLoadingComplete: true,
        });
    };

    _onShowGeolocateModalHidden = () => {
        this.setState({
            showGeolocateModal: false,
        });
    };

    _onListScroll = () => {
        const {sortBy} = this.props;
        const {showListLoader} = this.state;

        if (
            !this._hasSeoContent &&
            !showListLoader &&
            sortBy !== 'price' &&
            this._spotListEl.scrollTop ===
                this._spotListEl.scrollHeight - this._spotListEl.offsetHeight
        ) {
            this._loadNextPage();
        }
    };

    _onEditClick = evt => {
        this.props.showMobileListView(true);
    };

    _onToggleInfoModal = evt => {
        this.setState((prevState, props) => {
            return {
                showInfoModal: !prevState.showInfoModal,
            };
        });
    };

    _cacheSelectors() {
        this._spotListEl = DOMUtils.el('.SpotListContainer');
    }

    _fetchCompleted() {
        this.setState({
            showListLoader: false,
            showLoadingComplete: false,
        });
    }

    _newSearch = ({dateTimeChanged = false, initial = false}) => {
        const {
            numActiveFilters,
            idealDistance,
            loadMobileSpots,
            onNoSpotsFound,
        } = this.props;

        if (!initial) {
            this.setState({
                showListLoader: true,
            });
        }

        loadMobileSpots({
            limit: 8,
            maxDistance: idealDistance,
            dateTimeChanged,
        }).then(response => {
            if (!this._spotLoaderEnabled) {
                return;
            }

            const {
                value: {results},
            } = response;

            if (!results || results.length < 4 || numActiveFilters > 0) {
                if (!results) {
                    this._minDistance = 100;
                }

                // Ccccccrazy repeat tries for additional spots when none found initially
                // to be updated when we have real search pagination
                this._loadNextPage({
                    onNoResults: () => {
                        this._loadNextPage({
                            onNoResults: () => {
                                this._loadNextPage({
                                    onNoResults: () => {
                                        this._onLoadingComplete();

                                        onNoSpotsFound(true);
                                    },
                                });
                            },
                        });
                    },
                });
            }
        });
    };

    _loadNextPage = ({onNoResults = null} = {}) => {
        const {
            spots,
            loadMobileSpots,
            numActiveFilters,
            idealDistance,
            mobilePaginationLimit,
        } = this.props;
        let minDistance = spots.length
            ? sortByLodash(spots, 'distance').reverse()[0].distance - 30
            : 0;

        if (this._pageCount > 9) {
            this._onLoadingComplete();

            return;
        }

        minDistance =
            this._minDistance && this._minDistance === minDistance
                ? minDistance + 500
                : minDistance < this._minDistance
                ? this._minDistance + 500
                : minDistance;

        this.setState({
            showListLoader: true,
        });

        const params = {
            minDistance,
            maxDistance: idealDistance,
        };

        if (mobilePaginationLimit) {
            params.limit = mobilePaginationLimit;
        }

        this._spotsLoader = APIUtils.makeCancelable(loadMobileSpots(params));
        this._spotsLoader.promise
            .then(({value}) => {
                const {results} = value;

                if (
                    (!results || !results.length || numActiveFilters > 0) &&
                    onNoResults
                ) {
                    onNoResults();
                }
            })
            .catch(error => {
                return error;
            });

        this._minDistance = minDistance;
        this._pageCount += 1;
    };

    _userPositionReturned = response => {
        if (!response.coords) {
            return false;
        }

        const {latitude, longitude} = response.coords;

        this.props.setNewSearchLocation({
            latitude,
            longitude,
            title: 'Current Location',
            geolocated: true,
        });

        this.setState({
            showGeolocateModal: false,
        });
    };

    render() {
        const {
            searchRequestId,
            searchType,
            isMonthly,
            starts,
            ends,
            activeSearchTitle,
            city,
            destination,
            event,
            selectedSpot,
            spots,
            findParkingNearMe,
            geolocated,
            isMobileListViewActive,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
        } = this.props;
        const {
            showListLoader,
            showLoadingComplete,
            showGeolocateModal,
            showInfoModal,
        } = this.state;
        const hasDestination = !isEmpty(destination);
        const searchClasses = classNames(
            'Search',
            {'Search-monthly': isMonthly},
            {'Search-list-view': isMobileListViewActive},
            {'Search-map-view': !isMobileListViewActive}
        );

        return (
            <>
                <div className="main" role="main">
                    <div className={searchClasses}>
                        <div className="search-results">
                            {searchRequestId ? (
                                <>
                                    {!isMobileListViewActive && (
                                        <>
                                            <div className="SearchMap-container">
                                                <SearchMap
                                                    isMobile
                                                    spots={spots}
                                                    findParkingNearMe={
                                                        findParkingNearMe
                                                    }
                                                    isSoldOutEnabled={
                                                        isSoldOutEnabled
                                                    }
                                                    onShowDetailsClick={
                                                        onShowDetailsClick
                                                    }
                                                />
                                            </div>
                                            <SpotListSearchControlsCondensed
                                                starts={starts}
                                                ends={ends}
                                                isMonthly={isMonthly}
                                                title={activeSearchTitle}
                                                onEditClick={this._onEditClick}
                                            />
                                            <QuickFilters className="QuickFilters-map" />
                                        </>
                                    )}
                                    {isMobileListViewActive && (
                                        <SpotListContainer
                                            isMobile
                                            searchType={searchType}
                                            isMonthly={isMonthly}
                                            starts={starts}
                                            ends={ends}
                                            activeSearchTitle={
                                                activeSearchTitle
                                            }
                                            city={city}
                                            destination={destination}
                                            event={event}
                                            spots={spots}
                                            showListLoader={showListLoader}
                                            showLoadingComplete={
                                                showLoadingComplete
                                            }
                                            showDestinationInfo={
                                                this._hasSeoContent
                                            }
                                            loadNextPage={this._loadNextPage}
                                            isEventListEnabled={
                                                isEventListEnabled
                                            }
                                            showEventList={showEventList}
                                            onToggleEventList={
                                                onToggleEventList
                                            }
                                            onToggleInfoModal={
                                                this._onToggleInfoModal
                                            }
                                            isLowInventoryEnabled={
                                                isLowInventoryEnabled
                                            }
                                            isSoldOutEnabled={isSoldOutEnabled}
                                            onShowDetailsClick={
                                                onShowDetailsClick
                                            }
                                        />
                                    )}
                                </>
                            ) : (
                                <SHLoader />
                            )}
                        </div>
                        {selectedSpot && (
                            <SpotDetails
                                isMobile
                                spot={selectedSpot}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                            />
                        )}
                    </div>
                </div>
                {!this._hasSeoContent && showInfoModal && (
                    <DestinationInfoModalMobile
                        title={
                            hasDestination
                                ? isMonthly
                                    ? `${destination.title} Monthly`
                                    : destination.title
                                : isMonthly
                                ? `${city.display_name} Monthly`
                                : city.display_name
                        }
                        address={
                            hasDestination ? destination.street_address : null
                        }
                        city={
                            hasDestination
                                ? destination.city
                                : city.display_name
                        }
                        state={hasDestination ? destination.state : city.state}
                        description={
                            hasDestination
                                ? isMonthly && destination.monthly_description
                                    ? destination.monthly_description
                                    : destination.description
                                : isMonthly && city.monthly_description
                                ? city.monthly_description
                                : city.description
                        }
                        event={event}
                        onHidden={this._onToggleInfoModal}
                    />
                )}
                {showGeolocateModal && !geolocated && (
                    <AlertModal
                        className="GeolocationModal"
                        title="Where ya headed?"
                        confirmButtonLabel="My Current Location"
                        showRejectButton
                        showClose={false}
                        rejectButtonLabel="Somewhere Else"
                        onHidden={this._onShowGeolocateModalHidden}
                        onConfirm={this._onConfirmGeolocate}
                        onReject={this._onRejectGeolocate}
                    />
                )}
                {showEventList && (
                    <EventList onToggleEventList={onToggleEventList} />
                )}
            </>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        filters: {numActive},
        spots: {isPending: areSpotsLoading},
        search: {
            data: {
                breadcrumbs: {
                    activeBreadcrumb: {label: activeSearchTitle},
                },
                searchType,
                geolocated,
                mobilePaginationLimit,
                isMobileListViewActive,
            },
        },
        searchRequest: {id, starts, ends},
    } = state;

    return {
        searchRequestId: id,
        searchType,
        activeSearchTitle,
        city,
        destination,
        event,
        starts,
        ends,
        isFetchComplete: !areSpotsLoading,
        geolocated,
        numActiveFilters: numActive,
        mobilePaginationLimit,
        isMobileListViewActive,
    };
};

const mapDispatchToProps = {
    loadMobileSpots: getSpotsMobile,
    setNewSearchLocation: setLocation,
    showMobileListView: toggleMobileListView,
    onNoSpotsFound: setNoSpotsFound,
};

const enhance = compose(
    WithSearch,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SearchMobile);
